// ============================================
// USER EDIT PAGE — Sadece status + emailVerified yönetimi
// ============================================

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { ArrowLeft, Save, Mail, CheckCircle } from 'lucide-react';
import { PageHeader, StatusBadge } from '@/components';
import { adminApi } from '@/services/api';
import { cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/users/$id/edit')({
  component: UserEditPage,
});

function UserEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    status: 'ACTIVE',
    emailVerified: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: userData, isLoading } = useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => adminApi.getUser(id),
  });

  const user = (userData as any)?.data || userData;

  useEffect(() => {
    if (user) {
      setFormData({
        status: user.status || 'ACTIVE',
        emailVerified: user.emailVerified || false,
      });
    }
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => adminApi.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      navigate({ to: `/users/${id}` });
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  const handleSubmit = () => {
    updateMutation.mutate({
      status: formData.status,
      emailVerified: formData.emailVerified,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  const statusOptions = [
    { value: 'ACTIVE', label: 'Aktif' },
    { value: 'INACTIVE', label: 'Pasif' },
    { value: 'SUSPENDED', label: 'Askıya Alındı' },
    { value: 'PENDING_VERIFICATION', label: 'Doğrulama Bekliyor' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kullanıcı Yönetimi"
        description={user?.email}
        actions={
          <Link
            to={`/users/${id}` as any}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Geri
          </Link>
        }
      />

      {/* Kullanıcı bilgi özeti (read-only) */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Kullanıcı Bilgileri</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">E-posta</p>
              <p className="font-medium text-gray-900">{user?.email || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Rol</p>
              <p className="font-medium text-gray-900">
                {user?.role === 'SEAFARER' ? 'Denizci' : user?.role === 'COMPANY' ? 'Şirket' : 'Yönetici'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Durum yönetimi */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Hesap Durumu</h3>
        
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm text-gray-500">Mevcut Durum:</span>
          <StatusBadge status={user?.status} dot />
        </div>

        <div className="max-w-sm">
          <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Durum</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* E-posta doğrulama */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">E-posta Doğrulama</h3>
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-maritime">
          <div className="flex items-center gap-3">
            <Mail className={cn('h-6 w-6', formData.emailVerified ? 'text-green-500' : 'text-gray-400')} />
            <div>
              <p className="font-medium text-gray-900">E-posta Doğrulandı</p>
              <p className="text-sm text-gray-500">
                {formData.emailVerified ? 'E-posta adresi doğrulanmış' : 'E-posta adresi doğrulanmamış'}
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={formData.emailVerified}
            onClick={() => setFormData({ ...formData, emailVerified: !formData.emailVerified })}
            className={cn(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              formData.emailVerified ? 'bg-green-500' : 'bg-gray-300'
            )}
          >
            <span className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              formData.emailVerified ? 'translate-x-6' : 'translate-x-1'
            )} />
          </button>
        </div>

        {errors.submit && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-maritime">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {updateMutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
